import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/design-system/components/ui/collapsible';
import { cn } from '@repo/design-system/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import type { InsuranceRequirements as InsuranceRequirementsType } from '../types';

interface InsuranceSection {
  title: string;
  items?: Array<{
    amount: string;
    description: string;
  }>;
  amount?: string;
  description?: string;
}

interface InsuranceRequirementsProps {
  insurance: InsuranceRequirementsType;
}

function isInsuranceSection(section: unknown): section is InsuranceSection {
  return typeof section === 'object' && section !== null && 'title' in section;
}

function generateRows(insurance: InsuranceRequirementsType) {
  const rows: Array<{ label: string; value: React.ReactNode; key: string }> =
    [];

  for (const [key, section] of Object.entries(insurance)) {
    if (key === 'certificateRequirements') continue;
    if (!isInsuranceSection(section)) continue;

    // If there are items, show them (and description if it exists)
    if (section.items?.length) {
      rows.push({
        label: section.title,
        value: (
          <div className="space-y-1">
            {section.description && (
              <div className="text-sm">{section.description}</div>
            )}
            {section.items.map((item, idx) => (
              <div key={idx} className="text-sm">
                <b>{item.amount}</b> {item.description}
              </div>
            ))}
          </div>
        ),
        key,
      });
    } else if (section.amount) {
      rows.push({
        label: section.title,
        value: (
          <div className="text-sm">
            <b>{section.amount}</b> {section.description}
          </div>
        ),
        key,
      });
    } else if (section.description) {
      rows.push({
        label: section.title,
        value: <div className="text-sm">{section.description}</div>,
        key,
      });
    }
  }

  return rows;
}

export function InsuranceRequirements({
  insurance,
}: InsuranceRequirementsProps) {
  const [isOpen, setIsOpen] = useState(true);
  const rows = generateRows(insurance);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="space-y-3 md:space-y-1 md:pt-6"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between">
        <h4 className="font-medium text-base">Coverage limits</h4>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 transition-transform',
            isOpen ? 'rotate-180' : ''
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="grid grid-cols-[180px_1fr] gap-y-4">
          {rows.map((row) => (
            <React.Fragment key={row.key}>
              <div className="text-muted-foreground text-sm">{row.label}</div>
              <div>{row.value}</div>
            </React.Fragment>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
